import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconApi,
  IconBrain,
  IconCreditCard,
  IconExternalLink,
  IconHeadset,
  IconListNumbers,
  IconMessage,
  IconMicrophone,
  IconPhone,
} from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { useOrg } from '@/hooks/use-org';
import {
  BillingInvoicesSubscription,
  BillingUsageRecordsSubscription,
  OrganizationBillingSubscription,
} from './-components/graphql';

const METRIC_LABELS: Record<string, { label: string; icon: ReactNode }> = {
  call_minutes: { label: 'Call Minutes', icon: <IconPhone size={20} /> },
  api_calls: { label: 'API Calls', icon: <IconApi size={20} /> },
  recordings: { label: 'Recordings', icon: <IconMicrophone size={20} /> },
  sms: { label: 'SMS Messages', icon: <IconMessage size={20} /> },
  ai_tokens: { label: 'AI Tokens', icon: <IconBrain size={20} /> },
  agent_minutes: { label: 'Agent Minutes', icon: <IconHeadset size={20} /> },
  queue_calls: { label: 'Queue Calls', icon: <IconListNumbers size={20} /> },
};

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  trialing: 'blue',
  past_due: 'orange',
  canceled: 'red',
};

const INVOICE_STATUS_COLORS: Record<string, string> = {
  paid: 'green',
  open: 'blue',
  draft: 'gray',
  void: 'gray',
  uncollectible: 'red',
};

type Subscription = {
  status: string;
  trialEndsAt: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  stripeSubscriptionId: string | null;
  planName: string;
};

export const Route = createFileRoute('/_authed/settings/billing')({
  component: SettingsBillingPage,
});

function NoSubscriptionCard() {
  return (
    <Paper p="lg" radius="lg" withBorder>
      <Group gap="md">
        <ThemeIcon size="lg" radius="md" variant="light" color="gray">
          <IconCreditCard size={20} />
        </ThemeIcon>
        <div>
          <Text fw={600}>No Active Subscription</Text>
          <Text size="sm" c="dimmed">
            Contact an administrator to set up billing for your organization.
          </Text>
        </div>
      </Group>
    </Paper>
  );
}

function StripeActions({ organizationId, onError }: { organizationId: string; onError: (message: string) => void }) {
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const openPortal = async () => {
    setPendingAction('portal');

    const { data, error } = await api.billing.portal.post({ organizationId, returnUrl: window.location.href });

    setPendingAction(null);

    if (error) return onError(getErrorMessage(error, 'Unable to open the billing portal'));

    window.location.href = data.url;
  };

  const cancel = async () => {
    setPendingAction('cancel');

    const { error } = await api.billing.subscription.cancel.post({ organizationId, cancelAtPeriodEnd: true });

    setPendingAction(null);

    if (error) onError(getErrorMessage(error, 'Unable to cancel the subscription'));
  };

  const reactivate = async () => {
    setPendingAction('reactivate');

    const { error } = await api.billing.subscription.reactivate.post({ organizationId });

    setPendingAction(null);

    if (error) onError(getErrorMessage(error, 'Unable to reactivate the subscription'));
  };

  return (
    <Group gap="xs">
      <Button
        variant="light"
        size="sm"
        rightSection={<IconExternalLink size={16} />}
        onClick={() => void openPortal()}
        loading={pendingAction === 'portal'}
      >
        Manage
      </Button>
      <Button variant="subtle" size="sm" onClick={() => void reactivate()} loading={pendingAction === 'reactivate'}>
        Reactivate
      </Button>
      <Button variant="subtle" color="red" size="sm" onClick={() => void cancel()} loading={pendingAction === 'cancel'}>
        Cancel
      </Button>
    </Group>
  );
}

function SubscriptionCard({
  subscription,
  organizationId,
  onError,
}: {
  subscription: Subscription;
  organizationId: string;
  onError: (message: string) => void;
}) {
  const trialEnd = subscription.trialEndsAt ? new Date(subscription.trialEndsAt).toLocaleDateString() : null;

  return (
    <Paper p="lg" radius="lg" withBorder>
      <Group justify="space-between">
        <Group gap="md">
          <ThemeIcon size="lg" radius="md" variant="light" color="indigo">
            <IconCreditCard size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600} size="lg">
              {subscription.planName}
            </Text>
            <Text size="sm" c="dimmed">
              Current billing period: {new Date(subscription.currentPeriodStart).toLocaleDateString()} -{' '}
              {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
            </Text>
          </div>
        </Group>
        <Group gap="md">
          <Badge color={STATUS_COLORS[subscription.status] ?? 'gray'} size="lg" variant="light">
            {subscription.status === 'trialing' && trialEnd
              ? `Trial ends ${trialEnd}`
              : subscription.status.replace('_', ' ')}
          </Badge>
          {subscription.stripeSubscriptionId && <StripeActions organizationId={organizationId} onError={onError} />}
        </Group>
      </Group>
    </Paper>
  );
}

function UsageCard({ metricType, quantity }: { metricType: string; quantity: number }) {
  const config = METRIC_LABELS[metricType];

  return (
    <Paper p="lg" radius="lg" withBorder>
      <Group justify="space-between" mb="xs">
        <Group gap="sm">
          <ThemeIcon size="md" radius="md" variant="light" color="indigo">
            {config?.icon ?? <IconApi size={20} />}
          </ThemeIcon>
          <Text fw={500}>{config?.label ?? metricType}</Text>
        </Group>
        <Text fw={600} size="lg">
          {quantity.toLocaleString()}
        </Text>
      </Group>
      <Progress value={0} size="sm" radius="xl" color="indigo" />
    </Paper>
  );
}

function Billing({ organizationId }: { organizationId: string }) {
  const [error, setError] = useState<string | null>(null);
  const [{ data: billingData }] = useSubscription({
    query: OrganizationBillingSubscription,
    variables: { organizationId },
  });
  const [{ data: usageData }] = useSubscription({
    query: BillingUsageRecordsSubscription,
    variables: { organizationId },
  });
  const [{ data: invoiceData }] = useSubscription({
    query: BillingInvoicesSubscription,
    variables: { organizationId },
  });

  if (!billingData || !usageData || !invoiceData) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const billing = billingData.organizationBilling[0] ?? null;
  const currentUsage = billing
    ? usageData.usageRecords.filter((record) => record.billingPeriodStart === billing.currentPeriodStart)
    : [];

  return (
    <Stack gap="xl">
      <div>
        <Title order={2}>Billing</Title>
        <Text c="dimmed" mt="xs">
          Manage your subscription and view usage
        </Text>
      </div>

      <ErrorAlert message={error} />

      {billing ? (
        <SubscriptionCard
          organizationId={organizationId}
          onError={setError}
          subscription={{
            status: billing.status,
            trialEndsAt: billing.trialEndsAt,
            currentPeriodStart: billing.currentPeriodStart,
            currentPeriodEnd: billing.currentPeriodEnd,
            stripeSubscriptionId: billing.stripeSubscriptionId,
            planName: billing.plan.name,
          }}
        />
      ) : (
        <NoSubscriptionCard />
      )}

      <div>
        <Title order={3} size="h3" mb="md">
          Current Period Usage
        </Title>
        {currentUsage.length === 0 ? (
          <Paper p="lg" radius="lg" withBorder>
            <Text c="dimmed" ta="center">
              No usage recorded this billing period.
            </Text>
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {currentUsage.map((record) => (
              <UsageCard key={record.id} metricType={record.metricType} quantity={record.quantity} />
            ))}
          </SimpleGrid>
        )}
      </div>

      <div>
        <Title order={3} size="h3" mb="md">
          Invoices
        </Title>
        {invoiceData.invoices.length === 0 ? (
          <Paper p="lg" radius="lg" withBorder>
            <Text c="dimmed" ta="center">
              No invoices yet.
            </Text>
          </Paper>
        ) : (
          <Paper p={0} radius="lg" style={{ overflow: 'hidden' }} withBorder>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Period</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Total</Table.Th>
                  <Table.Th>Paid</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {invoiceData.invoices.map((invoice) => (
                  <Table.Tr key={invoice.id}>
                    <Table.Td>
                      <Text size="sm">
                        {new Date(invoice.billingPeriodStart).toLocaleDateString()} -{' '}
                        {new Date(invoice.billingPeriodEnd).toLocaleDateString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={INVOICE_STATUS_COLORS[invoice.status] ?? 'gray'}>
                        {invoice.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{invoice.total.toLocaleString()}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {invoice.paidAt ? new Date(invoice.paidAt).toLocaleDateString() : '—'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </div>
    </Stack>
  );
}

function SettingsBillingPage() {
  const { organizationId, isLoading } = useOrg();

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!organizationId) {
    return (
      <Center h="50vh">
        <Text c="dimmed">No organization selected</Text>
      </Center>
    );
  }

  return <Billing organizationId={organizationId} />;
}
